import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from './ui/select';

const INITIAL_FORM = {
  name: '',
  address: '',
  description: '',
  bedrooms: 0,
  bathrooms: 0,
  property_type: '',
};

const PropertyModal = ({ isOpen, onClose, onSave, property, saving }) => {
  const [formData, setFormData] = useState(INITIAL_FORM);

  const isEditing = !!property;

  useEffect(() => {
    if (property) {
      setFormData({
        name: property.name || '',
        address: property.address || '',
        description: property.description || '',
        bedrooms: property.bedrooms ?? 0,
        bathrooms: property.bathrooms ?? 0,
        property_type: property.property_type || '',
      });
    } else {
      setFormData(INITIAL_FORM);
    }
  }, [property, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      name: formData.name,
      address: formData.address,
      description: formData.description,
      bedrooms: formData.bedrooms,
      bathrooms: formData.bathrooms,
      property_type: formData.property_type,
    });
  };

  const update = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Property' : 'Add New Property'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the details for this property.'
              : 'Fill in the details to add a new property.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 pt-2">
          <div className="space-y-2">
            <Label htmlFor="prop-name">Property Name</Label>
            <Input
              id="prop-name"
              required
              value={formData.name}
              onChange={(e) => update('name', e.target.value)}
              placeholder="e.g., Sunset Villa"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="prop-address">Address</Label>
            <Input
              id="prop-address"
              required
              value={formData.address}
              onChange={(e) => update('address', e.target.value)}
              placeholder="e.g., 123 Main St, Ames, IA 50014"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="prop-description">Description</Label>
            <Input
              id="prop-description"
              value={formData.description}
              onChange={(e) => update('description', e.target.value)}
              placeholder="Brief description of the property"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="prop-beds">Bedrooms</Label>
              <Input
                id="prop-beds"
                type="number"
                min="0"
                value={formData.bedrooms}
                onChange={(e) => update('bedrooms', parseInt(e.target.value) || 0)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="prop-baths">Bathrooms</Label>
              <Input
                id="prop-baths"
                type="number"
                min="0"
                value={formData.bathrooms}
                onChange={(e) => update('bathrooms', parseInt(e.target.value) || 0)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="prop-type">Type</Label>
              <Select
                value={formData.property_type}
                onValueChange={(val) => update('property_type', val)}
              >
                <SelectTrigger id="prop-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="apartment">Apartment</SelectItem>
                  <SelectItem value="house">House</SelectItem>
                  <SelectItem value="ranch">Ranch</SelectItem>
                  <SelectItem value="condo">Condo</SelectItem>
                  <SelectItem value="townhouse">Townhouse</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#16A34A] hover:bg-[#15803D] text-white"
            >
              {saving
                ? 'Saving…'
                : isEditing
                ? 'Save Changes'
                : 'Add Property'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PropertyModal;
